import { Button, Checkbox, Classes, Dialog, FormGroup, InputGroup, Intent, HTMLSelect, MenuItem, Tooltip } from '@blueprintjs/core'
import { BotConfig } from 'enablex/sdk'
import Select from 'react-select'
import { FormFields, lang, toast } from 'enablex/shared'
import React, { FC, useEffect, useState } from 'react'
import api from '~/app/api'
import CloseIcon from '~/icons/CloseIcon'
import { InfoTip } from './InfoTip'

const NOT_SELECTED = 'Not Selected'

interface Props {
  isOpen: boolean
  botConfig: BotConfig
  linkedAgents: any[]
  toggle: () => void
  onUpdate: (agents: any[]) => void
  profile?: any // Add profile prop to access current user information
}

export const AgentsListView: FC<Props> = props => {
  const [botName, setbotName] = useState<string>()
  const [botLabels, setBotLabels] = useState<string[]>([])
  const [selectedItems, setSelectedItems] = useState<any>([])
  const [truncatedCheckboxes, setTruncatedCheckboxes] = useState({})
  const [initialAgents, setInitialAgents] = useState<{ value: string; checked: boolean; currentLabel: string[]; isSupervisor: boolean; disabled?: boolean }[]>([])

  // Get current user information
  const currentUserEmail = props.profile?.email?.toLowerCase()
  const currentUserRole = props.profile?.isSupervisor ? 'supervisor' : 'agent'
  const isCurrentUserSupervisor = currentUserRole === 'supervisor'

  useEffect(() => {
    setbotName(props.botConfig.name)
    setBotLabels(props.botConfig.labels ? [...props.botConfig.labels] : [])
  }, [props.isOpen])

  useEffect(() => {
    async function fetchData() {
      const agents: { value: string; checked: boolean; currentLabel: string[]; isSupervisor: boolean; disabled?: boolean }[] = []
      try {
        const axios = api.getSecured({ useV1: false })
        const { data } = await axios.get('admin/workspace/collaborators?roles=agent')

        for (let i = 0; i < data.payload.length; i++) {
          const user = data.payload[i]

          const agent = props.linkedAgents.find(item => item.agentId === user.email)

          // Determine if this agent should be disabled for the current supervisor
          let disabled = false
          if (isCurrentUserSupervisor) {
            // For supervisors, allow them to modify:
            // 1. Themselves (current user)
            // 2. Agents assigned to them (supervisor_id matches current user email)
            // Disable all others (other supervisors and agents not assigned to them)
            if (user.role === 'supervisor') {
              // Allow if it's the current supervisor, disable other supervisors
              disabled = user.email !== currentUserEmail
            } else if (user.role === 'agent') {
              // Allow if agent is assigned to current supervisor, disable if not
              disabled = user.supervisor_id !== currentUserEmail
            }
          }

          // Log all agents for debugging
          agents.push({
            value: user.email,
            checked: agent ? true : false,
            currentLabel: agent ? agent.agentSkill : [],
            isSupervisor: user.role === 'supervisor',
            disabled
          })
        }
        setInitialAgents(agents)

        // Log summary of disabled/enabled agents
        const disabledAgents = agents.filter(agent => agent.disabled)
        const enabledAgents = agents.filter(agent => !agent.disabled)

      } catch (error) {
        setInitialAgents([])
      }
    }
    void fetchData()
  }, [props.isOpen, isCurrentUserSupervisor, currentUserEmail])

  useEffect(() => {
    const newTruncatedCheckboxes = {}
    initialAgents.forEach((agent, index) => {
      const span = document.createElement('span')
      span.style.visibility = 'hidden'
      span.style.whiteSpace = 'nowrap'
      span.style.display = 'inline-block'
      span.style.position = 'absolute'
      span.style.zIndex = '-1'
      span.style.paddingRight = '10px'
      span.textContent = `${agent.value}${agent.isSupervisor ? ' (S)' : ''}`
      document.body.appendChild(span)
      const isTruncated = span.scrollWidth > 180 // Assuming 250px is the width of the checkbox label
      document.body.removeChild(span)
      newTruncatedCheckboxes[index] = isTruncated
    })
    setTruncatedCheckboxes(newTruncatedCheckboxes)
  }, [initialAgents])

  const handleCheckboxChange = (index: number) => {
    const updatedAgents = [...initialAgents]
    // Only allow changes if the agent is not disabled
    if (!updatedAgents[index].disabled) {
      updatedAgents[index].checked = !updatedAgents[index].checked
      setInitialAgents(updatedAgents)
    } else {
    }
  }

  const handleSelectChange = (event, index) => {
    const updatedAgents = [...initialAgents]
    if (event.target.value === NOT_SELECTED) {
      updatedAgents[index].currentLabel = []
    } else {
      updatedAgents[index].currentLabel = [event.target.value]
    }
    setInitialAgents(updatedAgents)
  }

  function arraysAreEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) {
      return false
    }
    return arr1.every((element, index) => element === arr2[index])
  }

  function generateOptions() {
    const botLabelArray = Array.isArray(botLabels) ? botLabels : JSON.parse(botLabels)
    return botLabelArray.map((botLabel) => {
      return {
        value: botLabel,
        label: botLabel
      }
    })
  }

  function generateValues(values) {
    const valuesArray = values || []
    return valuesArray.map(value => ({
      value,
      label: value
    }))
  }

  function handleTagSelect(selectedOptions, index) {
    const updatedAgents = [...initialAgents]
    updatedAgents[index].currentLabel = selectedOptions.map(selectedOption => selectedOption.value)
    setInitialAgents(updatedAgents)
  }

  const submit = async event => {
    event.preventDefault()
    const checkedAgents = initialAgents.filter(agent => agent.checked)
    const unCheckedAgents = initialAgents.filter(agent => !agent.checked)
    //const checkedAgentValues: string[] = checkedAgents.map(agent => agent.value)
    const uncheckedAgentValues: string[] = unCheckedAgents.map(agent => agent.value)
    const checkedAgentwithLabels: any[] = checkedAgents.map(agent => {
      return { 'agentId': agent.value, 'agentSkill': agent.currentLabel }
    })
    //const areEqual = arraysAreEqual(checkedAgentValues, props.linkedAgents)
    // if (areEqual) {
    //   toast.warning(lang.tr('admin.AgentsLinkedWarning'))
    //   return
    // }
    try {
      const axios = api.getSecured({ useV1: false })
      const payload = {
        selectedAgents: checkedAgentwithLabels,
        removedAgents: uncheckedAgentValues
      }
      await axios.post(`admin/workspace/bots/${props.botConfig.id}/setAgentsForBot`, payload)
      props.toggle()
      props.onUpdate(checkedAgentwithLabels)

      toast.success(lang.tr('admin.AgentsLinkedSuccessfully'))
    } catch (err) {
      toast.failure(lang.tr('admin.errorUpdatingAgents', { msg: err.message }))
    }
  }

  const listStyle = {
    display: 'flex',
    padding: '5px 0px',
    justifyContent: 'space-between',
    alignItems: 'center'
  }

  return (
    <Dialog
      //title={' Link Agents to ' + props.botConfig.name}
      title={<div style={{ display: 'flex', alignItems: 'center' }}><span>Link Agents</span><InfoTip customStyle={{ display: 'flex', height: '30px' }} info={lang.tr('admin.LinkAgentsInfo')} /></div>}
      // icon={<UserIcon />}
      isOpen={props.isOpen}
      onClose={props.toggle}
      transitionDuration={0}
      canOutsideClickClose={false}
      style={{ width: '600px' }}
    >
      {initialAgents.length ?
        <div style={{ ...listStyle, fontFamily: 'Barlow SemiBold', fontSize: 'medium', margin: '10px 20px 0px 10px' }}>
          <span>Agent</span>
          <span>Skills</span>
        </div> :
        <></>}
      <div className={Classes.DIALOG_BODY} style={{ overflow: 'auto', position: 'relative', margin: '10px', paddingRight: '4px' }}>
        <div style={{ height: '100%' }}>
          <ul style={{ listStyleType: 'none', fontSize: '22px', padding: '0 5px 0 0', margin: '0px' }}>
            {initialAgents.map((agent, index) => {
              const checkbox = (
                <Checkbox
                  style={{
                    paddingTop: '5px',
                    paddingRight: '10px',
                    width: '250px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: agent.disabled ? '#182026' : undefined,
                  }}
                  checked={agent.checked}
                  disabled={agent.disabled}
                  label={`${agent.value}${agent.disabled ? ' (Not under your supervision)' : ''}`}
                  large={true}
                  onChange={() => handleCheckboxChange(index)}
                />
              )

              return (
                <li key={index} style={listStyle}>
                  {truncatedCheckboxes[index] ? (
                    <Tooltip content={`${agent.value}${agent.disabled ? ' (Not under your supervision)' : ''}`}>
                      {checkbox}
                    </Tooltip>
                  ) : (
                    checkbox
                  )}
                  <FormGroup style={{ margin: '0px' }}>
                    <Select
                      isDisabled={!agent.checked || agent.disabled}
                      maxMenuHeight={200}
                      isMulti
                      closeMenuOnSelect={false}
                      options={generateOptions()}
                      value={generateValues(agent.currentLabel)}
                      onChange={(selectedOptions) => handleTagSelect(selectedOptions, index)}
                      menuPosition="fixed"
                      styles={{
                        container: (styles) => ({
                          ...styles,
                          width: '100%',
                          fontSize: '12px',
                        }),
                        control: (styles) => ({
                          ...styles,
                          minHeight: '30px',
                          width: '250px',
                          backgroundColor: agent.disabled ? '#f5f8fa' : 'white',
                          borderColor: agent.disabled ? '#a3adc8' : '#d3d8de',
                        }),
                        placeholder: (styles) => ({
                          ...styles,
                          color: agent.disabled ? '#5c7080' : '#5c7080',
                        }),
                        singleValue: (styles) => ({
                          ...styles,
                          color: agent.disabled ? '#182026' : '#182026',
                        }),
                      }}
                    />
                  </FormGroup>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button
            id="btn-submit-update-user"
            onClick={submit}
            text={lang.tr('save')}
            tabIndex={3}
            large={true}
            intent={Intent.PRIMARY}
            className={'btn-add-update-agents'}
          />
        </div>
      </div>
    </Dialog>
  )
}
